import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { FileSizePipe, SharedModule } from '@aiwyn/shared';
// Reusing the shared file-icon mapping that already powers `awn-file-item-icon`
// in the legacy file-upload-job-item — keeps the icon catalog consistent
// across the app and avoids duplicating the extension → icon table.
import { getFileIcon } from 'src/app/shared/components/file-manager/file-manager.utils';
import { CxpDeliverableFile } from '../../../../models';
import { CxpRelativeTimePipe } from '../../../../shared';
import { CxpDeliverableActionsService } from '../../../../services';

/**
 * Direct injection of `CxpDeliverableActionsService` is intentional and
 * matches the "thin action service" allowance in the `cxp-architecture`
 * skill — the service is stateless w.r.t. module state, the call is a
 * fire-and-forget side effect, and both arguments (`deliverableId`, `file`)
 * arrive as inputs so the container has no orchestration to add. Bubbling
 * `previewRequested` / `downloadRequested` through `files-list` → container
 * was ceremony, not value.
 */
@Component({
  selector: 'cxp-deliverable-file-row',
  templateUrl: './deliverable-file-row.component.html',
  styleUrls: ['./deliverable-file-row.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SharedModule, FileSizePipe, CxpRelativeTimePipe],
})
export class DeliverableFileRowComponent {
  private readonly actionsService = inject(CxpDeliverableActionsService);

  readonly deliverableId = input.required<number>();
  readonly file = input.required<CxpDeliverableFile>();

  readonly iconName = computed(() => {
    const extension = this.file().name?.toLowerCase().split('.').at(-1);
    return getFileIcon(extension ?? '');
  });

  readonly isDownloading = computed(() => this.actionsService.isFileDownloading(this.file().id));

  onPreviewClicked(): void {
    this.actionsService.previewFile(this.deliverableId(), this.file());
  }

  onDownloadClicked(): void {
    if (this.isDownloading()) return;
    this.actionsService.downloadOne(this.deliverableId(), this.file());
  }
}
